import { DialogComponent } from '@theme/dialog';
import { QuoteList, QuoteUpdateEvent } from '@theme/quote-list';

/**
 * Slide-out drawer listing the customer's quote items, with quantity controls and a
 * "Request Quote" link to /pages/request-quote. Mirrors the cart drawer but is rendered
 * entirely client-side from the localStorage quote list.
 *
 * @extends {DialogComponent}
 */
class QuoteDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(QuoteUpdateEvent.eventName, this.#render);
    this.addEventListener('click', this.#handleItemAction);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(QuoteUpdateEvent.eventName, this.#render);
    this.removeEventListener('click', this.#handleItemAction);
  }

  /** Opens the drawer (declarative `on:click="/open"`). */
  open() {
    this.showDialog();
  }

  /** Closes the drawer (declarative `on:click="quote-drawer-component/close"`). */
  close() {
    this.closeDialog();
  }

  /**
   * Delegated handler for the +/- and remove buttons inside the rendered list.
   * @param {Event} event
   */
  #handleItemAction = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-quote-action]') : null;
    if (!target) return;

    event.preventDefault();
    const id = target.getAttribute('data-quote-id');
    if (!id) return;

    const action = target.getAttribute('data-quote-action');
    const current = QuoteList.items().find((item) => String(item.id) === String(id));
    const quantity = current ? Number(current.quantity) || 1 : 0;

    if (action === 'increment') QuoteList.updateQuantity(id, quantity + 1);
    else if (action === 'decrement') QuoteList.updateQuantity(id, quantity - 1);
    else if (action === 'remove') QuoteList.remove(id);
  };

  /** Re-renders the drawer body from the current quote list. */
  #render = () => {
    const list = this.querySelector('[data-quote-drawer-items]');
    const footer = this.querySelector('[data-quote-drawer-footer]');
    const emptyState = this.querySelector('[data-quote-drawer-empty]');
    if (!list) return;

    const items = QuoteList.items();
    const hasItems = items.length > 0;

    if (emptyState) emptyState.classList.toggle('hidden', hasItems);
    if (footer) footer.classList.toggle('hidden', !hasItems);

    list.innerHTML = hasItems ? items.map((item) => this.#renderItem(item)).join('') : '';
  };

  /**
   * Builds the markup for a single quote line.
   * @param {import('@theme/quote-list').QuoteItem} item
   * @returns {string}
   */
  #renderItem(item) {
    const image = item.image
      ? `<img class="quote-drawer__item-image" src="${escapeAttr(item.image)}" alt="" width="64" height="64" loading="lazy">`
      : '<span class="quote-drawer__item-image quote-drawer__item-image--empty"></span>';
    const variant = item.variantTitle ? `<div class="quote-drawer__item-variant">${escapeHtml(item.variantTitle)}</div>` : '';
    const sku = item.sku ? `<div class="quote-drawer__item-sku">SKU: ${escapeHtml(item.sku)}</div>` : '';
    const id = escapeAttr(String(item.id));
    const quantity = Number(item.quantity) || 1;

    return `
      <div class="quote-drawer__item">
        <a href="${escapeAttr(item.url || '#')}" class="quote-drawer__item-media">${image}</a>
        <div class="quote-drawer__item-info">
          <a href="${escapeAttr(item.url || '#')}" class="quote-drawer__item-title">${escapeHtml(item.title || '')}</a>
          ${variant}
          ${sku}
          <div class="quote-drawer__item-controls">
            <div class="quote-drawer__qty">
              <button type="button" class="quote-drawer__qty-btn" data-quote-action="decrement" data-quote-id="${id}" aria-label="Decrease quantity">&minus;</button>
              <span class="quote-drawer__qty-value">${quantity}</span>
              <button type="button" class="quote-drawer__qty-btn" data-quote-action="increment" data-quote-id="${id}" aria-label="Increase quantity">+</button>
            </div>
            <button type="button" class="quote-drawer__remove button-unstyled" data-quote-action="remove" data-quote-id="${id}">Remove</button>
          </div>
        </div>
      </div>
    `;
  }
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] ?? char;
  });
}

/** @param {string} value */
function escapeAttr(value) {
  return escapeHtml(value);
}

if (!customElements.get('quote-drawer-component')) {
  customElements.define('quote-drawer-component', QuoteDrawerComponent);
}
